import {useEffect,useMemo,useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography
} from "@mui/material";
import {
  ArrowForwardRounded,
  AccessTimeRounded,
  CalendarMonthRounded,
  PlaceRounded
} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {useAppTheme} from "../../context/ThemeContext";
import stadiumImg from "../../static/Home/stadium.webp";

const ENDPOINT="/matches/public/upcoming/";
const FALLBACK_TOURNAMENT_SLUG="nexus-cup-2026";

function formatFecha(value){
  if(!value) return "Fecha por confirmar";

  const [year,month,day]=value.split("-").map(Number);
  const date=new Date(year,month-1,day);

  return new Intl.DateTimeFormat("es-EC",{
    weekday:"long",
    day:"numeric",
    month:"long"
  }).format(date);
}

function MatchCard({match,colors,typography,shape}){
  const tournamentSlug=match?.tournament?.slug||FALLBACK_TOURNAMENT_SLUG;
  const phaseName=match?.phase?.name||"Próximo partido";
  const tournamentName=match?.tournament?.name||"Torneo";
  const local=match?.local||{};
  const visitor=match?.visitor||{};
  const venue=match?.venue?.name||"Cancha por confirmar";
  const hora=match?.start_time||"--:--";
  const fechaTexto=formatFecha(match?.date);

  return(
    <Box
      sx={{
        width:"100%",
        borderRadius:shape.radiusLarge,
        border:`1px solid rgba(255,255,255,.14)`,
        background:"linear-gradient(180deg,rgba(6,19,45,.78) 0%,rgba(5,16,39,.86) 100%)",
        backdropFilter:"blur(8px)",
        overflow:"hidden"
      }}
    >
      <Box
        sx={{
          px:{xs:2,sm:2.5,md:3},
          py:1.5,
          borderBottom:`1px solid rgba(255,255,255,.10)`,
          display:"flex",
          alignItems:"center",
          justifyContent:"space-between",
          gap:2,
          flexWrap:"wrap"
        }}
      >
        <Box sx={{minWidth:0}}>
          <Typography
            sx={{
              fontSize:{xs:12,sm:13},
              fontWeight:600,
              color:"#FFFFFF",
              lineHeight:1.3
            }}
          >
            {tournamentName}
          </Typography>

          <Typography
            sx={{
              mt:0.25,
              fontSize:{xs:11,sm:12},
              fontWeight:400,
              color:"rgba(226,235,247,.78)",
              lineHeight:1.3
            }}
          >
            {phaseName}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px:{xs:2,sm:2.5,md:3},
          py:{xs:2.2,sm:2.5,md:3}
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={{xs:1.2,sm:2}}
        >
          <Box
            sx={{
              flex:1,
              minWidth:0,
              display:"flex",
              flexDirection:"column",
              alignItems:"center",
              textAlign:"center"
            }}
          >
            <Box
              sx={{
                width:{xs:60,sm:70,md:76},
                height:{xs:60,sm:70,md:76},
                borderRadius:"50%",
                border:"1px solid rgba(255,255,255,.12)",
                backgroundColor:"rgba(255,255,255,.04)",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                overflow:"hidden",
                p:1.1
              }}
            >
              {local.logo?(
                <Box
                  component="img"
                  src={local.logo}
                  alt={local.name||"Equipo local"}
                  sx={{
                    width:"100%",
                    height:"100%",
                    objectFit:"contain"
                  }}
                />
              ):(
                <Typography
                  sx={{
                    fontSize:{xs:20,sm:24},
                    fontWeight:600,
                    color:"#FFFFFF"
                  }}
                >
                  {String(local.name||"L").charAt(0)}
                </Typography>
              )}
            </Box>

            <Typography
              sx={{
                mt:1.3,
                fontSize:{xs:14,sm:15,md:16},
                fontWeight:600,
                color:"#FFFFFF",
                lineHeight:1.35,
                textWrap:"balance"
              }}
            >
              {local.name||"Equipo local"}
            </Typography>
          </Box>

          <Box
            sx={{
              px:{xs:0.5,sm:1},
              display:"flex",
              flexDirection:"column",
              alignItems:"center",
              justifyContent:"center",
              flexShrink:0
            }}
          >
            <Typography
              sx={{
                fontSize:{xs:24,sm:28,md:32},
                fontWeight:600,
                color:"#3460C9",
                lineHeight:1
              }}
            >
              VS
            </Typography>

            <Box
              sx={{
                mt:1.1,
                px:1.2,
                py:0.55,
                borderRadius:"999px",
                border:"1px solid rgba(255,255,255,.12)",
                backgroundColor:"rgba(255,255,255,.04)"
              }}
            >
              <Typography
                sx={{
                  fontSize:{xs:10,sm:11},
                  fontWeight:500,
                  color:"rgba(226,235,247,.82)",
                  lineHeight:1
                }}
              >
                Semifinal
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex:1,
              minWidth:0,
              display:"flex",
              flexDirection:"column",
              alignItems:"center",
              textAlign:"center"
            }}
          >
            <Box
              sx={{
                width:{xs:60,sm:70,md:76},
                height:{xs:60,sm:70,md:76},
                borderRadius:"50%",
                border:"1px solid rgba(255,255,255,.12)",
                backgroundColor:"rgba(255,255,255,.04)",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                overflow:"hidden",
                p:1.1
              }}
            >
              {visitor.logo?(
                <Box
                  component="img"
                  src={visitor.logo}
                  alt={visitor.name||"Equipo visitante"}
                  sx={{
                    width:"100%",
                    height:"100%",
                    objectFit:"contain"
                  }}
                />
              ):(
                <Typography
                  sx={{
                    fontSize:{xs:20,sm:24},
                    fontWeight:600,
                    color:"#FFFFFF"
                  }}
                >
                  {String(visitor.name||"V").charAt(0)}
                </Typography>
              )}
            </Box>

            <Typography
              sx={{
                mt:1.3,
                fontSize:{xs:14,sm:15,md:16},
                fontWeight:600,
                color:"#FFFFFF",
                lineHeight:1.35,
                textWrap:"balance"
              }}
            >
              {visitor.name||"Equipo visitante"}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{xs:"column",sm:"row"}}
          spacing={{xs:1.2,sm:2.2}}
          sx={{
            mt:{xs:2.2,sm:2.6,md:3},
            pt:{xs:1.8,sm:2},
            borderTop:`1px solid rgba(255,255,255,.10)`,
            alignItems:{xs:"flex-start",sm:"center"},
            justifyContent:"space-between"
          }}
        >
          <Stack direction="row" spacing={1.1} alignItems="center">
            <CalendarMonthRounded sx={{fontSize:18,color:"#3460C9"}}/>
            <Typography
              sx={{
                fontSize:{xs:12,sm:13},
                fontWeight:500,
                color:"#E2EBF7",
                lineHeight:1.4
              }}
            >
              {fechaTexto}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.1} alignItems="center">
            <AccessTimeRounded sx={{fontSize:18,color:"#3460C9"}}/>
            <Typography
              sx={{
                fontSize:{xs:12,sm:13},
                fontWeight:500,
                color:"#E2EBF7",
                lineHeight:1.4
              }}
            >
              {hora}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={1.1}
            alignItems="center"
            sx={{minWidth:0}}
          >
            <PlaceRounded sx={{fontSize:18,color:"#3460C9",flexShrink:0}}/>
            <Typography
              sx={{
                fontSize:{xs:12,sm:13},
                fontWeight:500,
                color:"#E2EBF7",
                lineHeight:1.4,
                minWidth:0
              }}
            >
              {venue}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default function UpcomingMatchesSection(){
  const {colors,typography,shape}=useAppTheme();

  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;

    const loadData=async()=>{
      try{
        setLoading(true);
        setError("");

        const response=await fetch(ENDPOINT,{
          method:"GET",
          headers:{
            "Content-Type":"application/json"
          }
        });

        if(!response.ok){
          throw new Error("No se pudieron cargar los próximos partidos.");
        }

        const data=await response.json();

        if(active){
          setItems(Array.isArray(data.results)?data.results:[]);
        }
      }catch(err){
        if(active){
          setError(err.message||"No se pudieron cargar los próximos partidos.");
          setItems([]);
        }
      }finally{
        if(active){
          setLoading(false);
        }
      }
    };

    loadData();

    return()=>{
      active=false;
    };
  },[]);

  const tournamentSlug=useMemo(()=>{
    return items?.[0]?.tournament?.slug||FALLBACK_TOURNAMENT_SLUG;
  },[items]);

  return(
    <Box
      component="section"
      aria-label="Próximos partidos"
      sx={{
        position:"relative",
        width:"100%",
        overflow:"hidden",
        backgroundColor:"#041127"
      }}
    >
      <Box
        sx={{
          position:"absolute",
          inset:0,
          backgroundImage:`url(${stadiumImg})`,
          backgroundSize:"cover",
          backgroundPosition:"center",
          backgroundRepeat:"no-repeat",
          zIndex:0
        }}
      />

      <Box
        sx={{
          position:"absolute",
          inset:0,
          zIndex:1,
          background:"linear-gradient(180deg,rgba(3,12,35,.95) 0%,rgba(4,15,43,.90) 34%,rgba(4,15,43,.88) 100%)"
        }}
      />

      <Container
        maxWidth="xl"
        sx={{
          position:"relative",
          zIndex:2,
          px:{xs:2.5,sm:4,md:5,lg:7},
          py:{xs:6,sm:7,md:8}
        }}
      >
        <Box
          sx={{
            maxWidth:{xs:"100%",sm:650,md:780},
            mx:"auto",
            textAlign:"center"
          }}
        >
          <Typography
            sx={{
              fontSize:{xs:28,sm:34,md:42},
              fontWeight:600,
              lineHeight:1.12,
              letterSpacing:"-0.03em",
              color:"#FFFFFF"
            }}
          >
            Próximos partidos
          </Typography>
        </Box>

        <Box sx={{mt:{xs:4,sm:4.5,md:5}}}>
          {loading?(
            <Box
              sx={{
                minHeight:260,
                display:"flex",
                alignItems:"center",
                justifyContent:"center"
              }}
            >
              <CircularProgress sx={{color:"#3460C9"}}/>
            </Box>
          ):error?(
            <Box
              sx={{
                maxWidth:780,
                mx:"auto",
                px:3,
                py:5,
                textAlign:"center",
                borderRadius:shape.radiusLarge,
                border:`1px solid rgba(255,255,255,.12)`,
                background:"rgba(6,19,45,.68)"
              }}
            >
              <Typography
                sx={{
                  fontSize:{xs:15,sm:16},
                  fontWeight:500,
                  color:"#FFFFFF"
                }}
              >
                {error}
              </Typography>
            </Box>
          ):items.length===0?(
            <Box
              sx={{
                maxWidth:780,
                mx:"auto",
                px:3,
                py:5,
                textAlign:"center",
                borderRadius:shape.radiusLarge,
                border:`1px solid rgba(255,255,255,.12)`,
                background:"rgba(6,19,45,.68)"
              }}
            >
              <Typography
                sx={{
                  fontSize:{xs:15,sm:16},
                  fontWeight:500,
                  color:"#FFFFFF"
                }}
              >
                No hay partidos próximos por mostrar en este momento.
              </Typography>
            </Box>
          ):(
            <Stack
              spacing={2}
              sx={{
                maxWidth:1100,
                mx:"auto"
              }}
            >
              {items.slice(0,2).map(match=>(
                <MatchCard
                  key={match.code}
                  match={match}
                  colors={colors}
                  typography={typography}
                  shape={shape}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            mt:{xs:3.5,sm:4,md:4.5},
            display:"flex",
            justifyContent:"center"
          }}
        >
          <Button
            component={Link}
            to={`/torneos/${tournamentSlug}/calendario`}
            endIcon={<ArrowForwardRounded sx={{fontSize:"18px !important"}}/>}
            sx={{
              minHeight:46,
              px:3,
              borderRadius:shape.radius,
              backgroundColor:"#1746A2",
              color:"#FFFFFF",
              fontSize:typography.sizes.sm,
              fontWeight:600,
              textTransform:"none",
              boxShadow:"none",
              "&:hover":{
                backgroundColor:"#103780",
                boxShadow:"none"
              }
            }}
          >
            Ver todos los partidos
          </Button>
        </Box>
      </Container>
    </Box>
  );
}